
import React, { useState } from "react";
import { RibaStage, Project } from "@/types";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { useProject } from "@/contexts/ProjectContext";
import { toast } from "@/hooks/use-toast";
import { FileUpload } from "./FileUpload";
import { Lock } from "lucide-react";

interface StageInputProps {
  project: Project;
  stage: RibaStage;
  onUpdate?: () => void;
}

// Define input fields by stage ID
const stageInputFields: Record<number, { name: string; type: "text" | "textarea" | "file" | "number"; label: string; description?: string }[]> = {
  0: [
    { name: "clientRequirements", type: "textarea", label: "Client Requirements", description: "Define core project requirements and objectives" },
    { name: "businessCase", type: "textarea", label: "Business Case", description: "Project justification and strategic goals" },
    { name: "siteInformation", type: "textarea", label: "Site Information", description: "Key details about the project site" },
    { name: "initialFeasibility", type: "file", label: "Initial Feasibility Studies", description: "Upload feasibility studies documents" }
  ],
  1: [
    { name: "projectBrief", type: "textarea", label: "Project Brief", description: "Details of objectives, sustainability goals, and spatial needs" },
    { name: "budget", type: "number", label: "Project Budget", description: "Estimated total project budget" },
    { name: "siteSurveys", type: "file", label: "Site Surveys", description: "Upload site survey documents" },
    { name: "projectProgramme", type: "textarea", label: "Project Programme", description: "Key milestones and programmed dates" },
    { name: "procurementStrategy", type: "textarea", label: "Procurement Strategy", description: "Approach to project procurement" }
  ],
  2: [
    { name: "architecturalConcept", type: "textarea", label: "Architectural Concept", description: "Description of the architectural approach" },
    { name: "outlineSpecification", type: "textarea", label: "Outline Specification", description: "Initial specification of materials and systems" },
    { name: "initialDesignStudies", type: "file", label: "Initial Design Studies", description: "Upload initial design studies" },
    { name: "planningConstraints", type: "file", label: "Site and Planning Constraints", description: "Upload planning constraint documents" },
    { name: "planningAdvice", type: "file", label: "Planning Advice", description: "Upload planning advice documents" }
  ],
  3: [
    { name: "spatialCoordination", type: "file", label: "Spatially Coordinated Design", description: "Upload spatially coordinated design files" },
    { name: "detailedSpecification", type: "file", label: "Detailed Specification", description: "Upload detailed specification documents" },
    { name: "planningSubmission", type: "file", label: "Planning Application", description: "Upload planning application documents" },
    { name: "costPlan", type: "file", label: "Updated Cost Plan", description: "Upload the updated cost plan" },
    { name: "designReports", type: "file", label: "Design Reports", description: "Upload technical design reports" }
  ],
  4: [
    { name: "technicalDesign", type: "file", label: "Technical Design", description: "Upload complete technical design documents" },
    { name: "finalSpecifications", type: "file", label: "Final Specifications", description: "Upload final detailed specifications" },
    { name: "regulatorySubmissions", type: "file", label: "Regulatory Submissions", description: "Upload building regulations and other regulatory submission documents" },
    { name: "tenderDocuments", type: "file", label: "Tender Documents", description: "Upload tender documentation if applicable" },
    { name: "contractorProcurement", type: "textarea", label: "Contractor Procurement", description: "Details of contractor procurement approach" },
  ],
  5: [
    { name: "constructionProgramme", type: "file", label: "Construction Programme", description: "Upload the detailed construction programme" },
    { name: "qualityControlPlan", type: "file", label: "Quality Control Plan", description: "Upload quality control and assurance plan" },
    { name: "healthSafetyPlan", type: "file", label: "Health & Safety Plan", description: "Upload health and safety documents" },
    { name: "siteLogistics", type: "file", label: "Site Logistics Plan", description: "Upload site logistics and management plan" },
    { name: "progressReports", type: "textarea", label: "Progress Reporting", description: "Define progress reporting frequency and format" }
  ],
  6: [
    { name: "handoverPlan", type: "file", label: "Handover Plan", description: "Upload project handover plan and schedule" },
    { name: "commissioningRecords", type: "file", label: "Commissioning Records", description: "Upload commissioning and testing records" },
    { name: "buildingManual", type: "file", label: "Building Manual", description: "Upload building manual and operation instructions" },
    { name: "assetRegister", type: "file", label: "Asset Register", description: "Upload detailed asset register" },
    { name: "defectsLog", type: "textarea", label: "Defects Log", description: "Initial log of identified defects" }
  ],
  7: [
    { name: "postOccupancyPlan", type: "file", label: "Post-Occupancy Plan", description: "Upload post-occupancy evaluation plan" },
    { name: "performanceMetrics", type: "textarea", label: "Performance Metrics", description: "Define key metrics for building performance evaluation" },
    { name: "facilityManagement", type: "file", label: "Facility Management Information", description: "Upload facility management documentation" },
    { name: "sustainabilityAssessment", type: "file", label: "Sustainability Assessment", description: "Upload sustainability outcomes verification" },
    { name: "lessonsLearned", type: "textarea", label: "Lessons Learned", description: "Document key lessons from project delivery" }
  ]
};

export default function StageInputs({ project, stage, onUpdate }: StageInputProps) {
  const { updateStage } = useProject();
  const [inputs, setInputs] = useState<Record<string, string>>({});
  const [isEditing, setIsEditing] = useState(false);
  
  // Check if this stage has defined inputs
  const stageFields = stageInputFields[stage.id] || [];
  
  const handleInputChange = (name: string, value: string) => {
    setInputs(prev => ({ ...prev, [name]: value }));
  };
  
  const handleSave = () => {
    // In a real implementation, would save to the project context or backend
    toast({
      title: "Inputs saved",
      description: `Saved inputs for Stage ${stage.id}: ${stage.name}`,
    });
    setIsEditing(false);
    
    if (onUpdate) {
      onUpdate();
    }
  };
  
  // Determine if the stage is locked (e.g., completed stages)
  const isLocked = stage.status === 'completed';
  
  if (stageFields.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>No Inputs Required</CardTitle>
          <CardDescription>This stage does not require any specific inputs.</CardDescription>
        </CardHeader>
      </Card>
    );
  }
  
  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between">
        <div>
          <CardTitle>Stage Inputs</CardTitle>
          <CardDescription>Required inputs for this stage</CardDescription>
        </div>
        {isLocked ? (
          <div className="flex items-center text-muted-foreground">
            <Lock className="mr-2 h-4 w-4" />
            <span>Inputs locked</span>
          </div>
        ) : (
          <Button onClick={() => setIsEditing(!isEditing)}>
            {isEditing ? "Cancel" : "Edit Inputs"}
          </Button>
        )}
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          {stageFields.map((field) => (
            <div key={field.name} className="space-y-2">
              <label className="text-sm font-medium" htmlFor={field.name}>
                {field.label}
              </label>
              {field.description && (
                <p className="text-xs text-muted-foreground">{field.description}</p>
              )}
              
              {field.type === "textarea" ? (
                <Textarea
                  id={field.name}
                  placeholder={`Enter ${field.label.toLowerCase()}`}
                  value={inputs[field.name] || ""}
                  onChange={(e) => handleInputChange(field.name, e.target.value)}
                  disabled={!isEditing || isLocked}
                  className="w-full"
                />
              ) : field.type === "file" ? (
                <FileUpload 
                  id={field.name}
                  disabled={!isEditing || isLocked}
                  onUpload={(url) => handleInputChange(field.name, url)}
                />
              ) : field.type === "number" ? (
                <Input
                  id={field.name}
                  type="number"
                  placeholder={`Enter ${field.label.toLowerCase()}`}
                  value={inputs[field.name] || ""}
                  onChange={(e) => handleInputChange(field.name, e.target.value)}
                  disabled={!isEditing || isLocked}
                  className="w-full"
                />
              ) : (
                <Input
                  id={field.name}
                  placeholder={`Enter ${field.label.toLowerCase()}`}
                  value={inputs[field.name] || ""}
                  onChange={(e) => handleInputChange(field.name, e.target.value)}
                  disabled={!isEditing || isLocked}
                  className="w-full"
                />
              )}
            </div>
          ))}
          
          {isEditing && (
            <div className="flex justify-end space-x-2">
              <Button variant="outline" onClick={() => setIsEditing(false)}>
                Cancel
              </Button>
              <Button onClick={handleSave}>
                Save Inputs
              </Button>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
